import {
    Alert,
    Container,
    convertFromBoolean,
    convertToBoolean,
    Form,
    FormDropDown,
    FormInput,
    FormRadio,
    getPageValidationInfo,
    useSizes,
} from 'metrix-common3';
import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

const ProfileInsuranceApplicant = (props) => {
    const sizes = useSizes();
    const [showOtherRelationship, setShowOtherRelationship] = useState(false);

    // biome-ignore lint/correctness/useExhaustiveDependencies: intentional - mimics componentDidMount behavior
    useEffect(() => {
        props.doGetPolicy();

        const pageHasTooltipInfo =
            props.tooltipInfo?.pages?.length && props.tooltipInfo.pages.some((p) => p.code === props.pageCode);
        if (!pageHasTooltipInfo) {
            props.doGetTooltipInfo(props.policy.product);
        }
    }, []);

    useEffect(() => {
        // Check if relationship is "Other" to show/hide the other relationship field
        const relationshipCode = props.policy?.secureMyOfferCustomInfo?.applicantRelationshipToSellerCode;
        setShowOtherRelationship(relationshipCode === 'Other');
    }, [props.policy?.secureMyOfferCustomInfo?.applicantRelationshipToSellerCode]);

    const { error, warning } = getPageValidationInfo(props.pageCode, props.validationInfo);
    const isCertificate = props.policy?.type?.toLowerCase() === 'certificate';

    const isSeller = props.policy?.secureMyOfferCustomInfo?.certificateOwnerTypeCode?.toLowerCase() === 'seller';
    const isRealtor =
        props.policy?.secureMyOfferCustomInfo?.certificateOwnerTypeCode?.toLowerCase() === 'realtorlawyer';
    const isBuyer = props.policy?.secureMyOfferCustomInfo?.certificateOwnerTypeCode?.toLowerCase() === 'buyer';
    const isAdmin = props.context?.session?.userType?.toLowerCase() === 'administrator';

    // Relationship-based visibility (for non-certificate types)
    const relationshipCode = props.policy?.secureMyOfferCustomInfo?.applicantRelationshipToSellerCode?.toLowerCase();
    const isSellerApplicant = props.policy?.secureMyOfferCustomInfo?.isSeller;
    const isNotSeller = isSellerApplicant === false;

    // Applicant Contact Details: not certificate + not seller + relationship is lawyer/other/mortgagebroker; certificate = hidden
    const showApplicantContactDetails =
        !isCertificate && isNotSeller && ['lawyer', 'other', 'mortgagebroker'].includes(relationshipCode);

    // Real Estate Agent Contact Details: not certificate + not seller + realtor; certificate + certificateOwner is realtorlawyer
    const showRealEstateAgentContactDetails =
        (!isCertificate && isNotSeller && relationshipCode === 'realtor') || (isCertificate && isRealtor);

    // Buyer Contact Details: not certificate = always visible; certificate + certificateOwner is buyer
    const showBuyerContactDetails = !isCertificate || (isCertificate && isBuyer);

    return (
        <React.Fragment>
            {(error.message.length !== 0 || warning.message.length !== 0) && (
                <Container top={sizes.md}>
                    {error.message.length !== 0 && <Alert mode='danger' header={error.title} items={error.message} />}
                    {warning.message.length !== 0 && (
                        <Alert mode='warning' header={warning.title} items={warning.message} />
                    )}
                </Container>
            )}
            {showApplicantContactDetails && (
                <Form
                    pageCode={props.pageCode}
                    sectionCode='contactDetails'
                    saveTooltipInfo={() => props.doUpsertTooltipInfo(props.policy.product)}
                    reduceTooltipInfoElement={props.doReduceTooltipInfoElement}
                    tooltipInfo={props.tooltipInfo}
                    validationInfo={props.validationInfo}
                    header={<FormattedMessage id='Profile_InsuranceApplicant.contactDetails' />}
                    label={<FormattedMessage id='Profile_InsuranceApplicant.enterContactInformation' />}
                    saveEvent={props.doUpsertPolicy}
                    cancelEvent={props.doGetPolicy}
                    allowEdit={props.allowEdit}
                >
                    <FormInput
                        fieldCode='firstName'
                        label={<FormattedMessage id='Profile_InsuranceApplicant.firstName' />}
                        value={props.policy?.secureMyOfferCustomInfo?.applicantFirstName}
                        changeEvent={(value) =>
                            props.doReducePolicyElement({ code: 'secureMyOfferCustomInfo.applicantFirstName', value })
                        }
                        allowEdit
                    />
                    <FormInput
                        fieldCode='lastName'
                        label={<FormattedMessage id='Profile_InsuranceApplicant.lastName' />}
                        value={props.policy?.secureMyOfferCustomInfo?.applicantLastName}
                        changeEvent={(value) =>
                            props.doReducePolicyElement({ code: 'secureMyOfferCustomInfo.applicantLastName', value })
                        }
                        allowEdit
                    />
                    <FormInput
                        fieldCode='email'
                        label={<FormattedMessage id='Profile_InsuranceApplicant.email' />}
                        value={props.policy?.secureMyOfferCustomInfo?.applicantEmail}
                        type='email'
                        placeholder='someone@example.com'
                        changeEvent={(value) =>
                            props.doReducePolicyElement({ code: 'secureMyOfferCustomInfo.applicantEmail', value })
                        }
                        allowEdit
                    />
                    <FormInput
                        fieldCode='phone'
                        label={<FormattedMessage id='Profile_InsuranceApplicant.phoneNumber' />}
                        value={props.policy?.secureMyOfferCustomInfo?.applicantPhone}
                        type='phone'
                        changeEvent={(value) =>
                            props.doReducePolicyElement({ code: 'secureMyOfferCustomInfo.applicantPhone', value })
                        }
                        allowEdit
                    />
                </Form>
            )}
            {showRealEstateAgentContactDetails && (
                <Form
                    pageCode={props.pageCode}
                    sectionCode='realEstateAgentContactDetails'
                    saveTooltipInfo={() => props.doUpsertTooltipInfo(props.policy.product)}
                    reduceTooltipInfoElement={props.doReduceTooltipInfoElement}
                    tooltipInfo={props.tooltipInfo}
                    validationInfo={props.validationInfo}
                    header={<FormattedMessage id='Profile_InsuranceApplicant.realEstateAgentContactDetails' />}
                    label={<FormattedMessage id='Profile_InsuranceApplicant.realEstateAgentContactInformation' />}
                    saveEvent={props.doUpsertPolicy}
                    cancelEvent={props.doGetPolicy}
                    allowEdit={props.allowEdit}
                >
                    <FormInput
                        fieldCode='realEstateAgentFirstName'
                        label={<FormattedMessage id='Profile_InsuranceApplicant.firstName' />}
                        value={props.policy?.secureMyOfferCustomInfo?.realEstateAgentFirstName}
                        changeEvent={(value) =>
                            props.doReducePolicyElement({
                                code: 'secureMyOfferCustomInfo.realEstateAgentFirstName',
                                value,
                            })
                        }
                        allowEdit
                    />
                    <FormInput
                        fieldCode='realEstateAgentLastName'
                        label={<FormattedMessage id='Profile_InsuranceApplicant.lastName' />}
                        value={props.policy?.secureMyOfferCustomInfo?.realEstateAgentLastName}
                        changeEvent={(value) =>
                            props.doReducePolicyElement({
                                code: 'secureMyOfferCustomInfo.realEstateAgentLastName',
                                value,
                            })
                        }
                        allowEdit
                    />
                    <FormInput
                        fieldCode='realEstateAgentEmail'
                        label={<FormattedMessage id='Profile_InsuranceApplicant.email' />}
                        value={props.policy?.secureMyOfferCustomInfo?.realEstateAgentEmail}
                        type='email'
                        placeholder='someone@example.com'
                        changeEvent={(value) =>
                            props.doReducePolicyElement({ code: 'secureMyOfferCustomInfo.realEstateAgentEmail', value })
                        }
                        allowEdit
                    />
                    <FormInput
                        fieldCode='realEstateAgentPhone'
                        label={<FormattedMessage id='Profile_InsuranceApplicant.phoneNumber' />}
                        value={props.policy?.secureMyOfferCustomInfo?.realEstateAgentPhone}
                        type='phone'
                        changeEvent={(value) =>
                            props.doReducePolicyElement({ code: 'secureMyOfferCustomInfo.realEstateAgentPhone', value })
                        }
                        allowEdit
                    />
                </Form>
            )}
            {showBuyerContactDetails && (
                <Form
                    pageCode={props.pageCode}
                    sectionCode='buyerContactDetails'
                    saveTooltipInfo={() => props.doUpsertTooltipInfo(props.policy.product)}
                    reduceTooltipInfoElement={props.doReduceTooltipInfoElement}
                    tooltipInfo={props.tooltipInfo}
                    validationInfo={props.validationInfo}
                    header={<FormattedMessage id='Profile_InsuranceApplicant.buyerContactDetails' />}
                    label={<FormattedMessage id='Profile_InsuranceApplicant.buyerContactInformation' />}
                    saveEvent={props.doUpsertPolicy}
                    cancelEvent={props.doGetPolicy}
                    allowEdit={props.allowEdit}
                >
                    <FormInput
                        fieldCode='buyerFirstName'
                        label={<FormattedMessage id='Profile_InsuranceApplicant.firstName' />}
                        value={props.policy?.secureMyOfferCustomInfo?.buyerFirstName}
                        changeEvent={(value) =>
                            props.doReducePolicyElement({ code: 'secureMyOfferCustomInfo.buyerFirstName', value })
                        }
                        allowEdit
                    />
                    <FormInput
                        fieldCode='buyerLastName'
                        label={<FormattedMessage id='Profile_InsuranceApplicant.lastName' />}
                        value={props.policy?.secureMyOfferCustomInfo?.buyerLastName}
                        changeEvent={(value) =>
                            props.doReducePolicyElement({ code: 'secureMyOfferCustomInfo.buyerLastName', value })
                        }
                        allowEdit
                    />
                    <FormInput
                        fieldCode='buyerEmail'
                        label={<FormattedMessage id='Profile_InsuranceApplicant.email' />}
                        value={props.policy?.secureMyOfferCustomInfo?.buyerEmail}
                        type='email'
                        placeholder='someone@example.com'
                        changeEvent={(value) =>
                            props.doReducePolicyElement({ code: 'secureMyOfferCustomInfo.buyerEmail', value })
                        }
                        allowEdit
                    />
                    <FormInput
                        fieldCode='buyerPhone'
                        label={<FormattedMessage id='Profile_InsuranceApplicant.phoneNumber' />}
                        value={props.policy?.secureMyOfferCustomInfo?.buyerPhone}
                        type='phone'
                        changeEvent={(value) =>
                            props.doReducePolicyElement({ code: 'secureMyOfferCustomInfo.buyerPhone', value })
                        }
                        allowEdit
                    />
                </Form>
            )}
            {isCertificate && (
                <Form
                    pageCode={props.pageCode}
                    sectionCode='certificateInformation'
                    saveTooltipInfo={() => props.doUpsertTooltipInfo(props.policy.product)}
                    reduceTooltipInfoElement={props.doReduceTooltipInfoElement}
                    tooltipInfo={props.tooltipInfo}
                    validationInfo={props.validationInfo}
                    header={<FormattedMessage id='Profile_InsuranceApplicant.certificateInformation' />}
                    saveEvent={props.doUpsertPolicy}
                    cancelEvent={props.doGetPolicy}
                    allowEdit={props.allowEdit}
                >
                    <FormDropDown
                        fieldCode='certificateOwnerTypeCode'
                        label={<FormattedMessage id='Profile_InsuranceApplicant.certificateOwnerType' />}
                        value={{
                            code: props.policy?.secureMyOfferCustomInfo?.certificateOwnerTypeCode,
                            value: props.policy.secureMyOfferCustomInfo?.certificateOwnerTypeName,
                        }}
                        references={props.reference?.references?.CertificateOwnerType}
                        referencesEvent={() => props.reference.doGetReferences('CertificateOwnerType')}
                        changeEvent={(value) => {
                            props.doReducePolicyElement({
                                code: 'secureMyOfferCustomInfo.certificateOwnerTypeCode',
                                value: value.code,
                            });
                        }}
                        allowEdit={false}
                    />
                    {isSeller && (
                        <FormInput
                            fieldCode='estimatedSalePrice'
                            label={<FormattedMessage id='Profile_InsuranceApplicant.estimatedSalePrice' />}
                            value={props.policy?.secureMyOfferCustomInfo?.estimatedSalePrice}
                            type='currency'
                            changeEvent={(value) =>
                                props.doReducePolicyElement({
                                    code: 'secureMyOfferCustomInfo.estimatedSalePrice',
                                    value,
                                })
                            }
                            allowEdit
                        />
                    )}
                    {isRealtor && (
                        <FormInput
                            fieldCode='minExpectedSaleAmount'
                            label={<FormattedMessage id='Profile_InsuranceApplicant.minExpectedSaleAmount' />}
                            value={props.policy?.secureMyOfferCustomInfo?.minExpectedSaleAmount}
                            type='currency'
                            changeEvent={(value) =>
                                props.doReducePolicyElement({
                                    code: 'secureMyOfferCustomInfo.minExpectedSaleAmount',
                                    value,
                                })
                            }
                            allowEdit
                        />
                    )}

                    {isRealtor && (
                        <FormInput
                            fieldCode='maxExpectedSaleAmount'
                            label={<FormattedMessage id='Profile_InsuranceApplicant.maxExpectedSaleAmount' />}
                            value={props.policy?.secureMyOfferCustomInfo?.maxExpectedSaleAmount}
                            type='currency'
                            changeEvent={(value) =>
                                props.doReducePolicyElement({
                                    code: 'secureMyOfferCustomInfo.maxExpectedSaleAmount',
                                    value,
                                })
                            }
                            allowEdit
                        />
                    )}

                    {isRealtor && (
                        <FormInput
                            fieldCode='minAmountFormatted'
                            label={<FormattedMessage id='Profile_InsuranceApplicant.minAmountFormatted' />}
                            value={props.policy?.secureMyOfferCustomInfo?.minAmountFormatted}
                            allowEdit={false}
                        />
                    )}

                    {isRealtor && (
                        <FormInput
                            fieldCode='maxAmountFormatted'
                            label={<FormattedMessage id='Profile_InsuranceApplicant.maxAmountFormatted' />}
                            value={props.policy?.secureMyOfferCustomInfo?.maxAmountFormatted}
                            allowEdit={false}
                        />
                    )}

                    {isBuyer && (
                        <FormInput
                            fieldCode='intendedOfferAmount'
                            label={<FormattedMessage id='Profile_InsuranceApplicant.intendedOfferAmount' />}
                            value={props.policy?.secureMyOfferCustomInfo?.intendedOfferAmount}
                            type='currency'
                            changeEvent={(value) =>
                                props.doReducePolicyElement({
                                    code: 'secureMyOfferCustomInfo.intendedOfferAmount',
                                    value,
                                })
                            }
                            allowEdit
                        />
                    )}

                    {isRealtor && isAdmin && (
                        <FormInput
                            fieldCode='preApprovalRangeMinAmountOverride'
                            label={
                                <FormattedMessage id='Profile_InsuranceApplicant.preApprovalRangeMinAmountOverride' />
                            }
                            value={props.policy?.secureMyOfferCustomInfo?.preApprovalRangeMinAmountOverride}
                            type='currency'
                            changeEvent={(value) =>
                                props.doReducePolicyElement({
                                    code: 'secureMyOfferCustomInfo.preApprovalRangeMinAmountOverride',
                                    value,
                                })
                            }
                            allowEdit
                        />
                    )}

                    {isRealtor && isAdmin && (
                        <FormInput
                            fieldCode='preApprovalRangeMaxAmountOverride'
                            label={
                                <FormattedMessage id='Profile_InsuranceApplicant.preApprovalRangeMaxAmountOverride' />
                            }
                            value={props.policy?.secureMyOfferCustomInfo?.preApprovalRangeMaxAmountOverride}
                            type='currency'
                            changeEvent={(value) =>
                                props.doReducePolicyElement({
                                    code: 'secureMyOfferCustomInfo.preApprovalRangeMaxAmountOverride',
                                    value,
                                })
                            }
                            allowEdit
                        />
                    )}
                </Form>
            )}
            <Form
                pageCode={props.pageCode}
                sectionCode='relationshipToSeller'
                saveTooltipInfo={() => props.doUpsertTooltipInfo(props.policy.product)}
                reduceTooltipInfoElement={props.doReduceTooltipInfoElement}
                tooltipInfo={props.tooltipInfo}
                validationInfo={props.validationInfo}
                header={<FormattedMessage id='Profile_InsuranceApplicant.relationshipToSeller' />}
                label={<FormattedMessage id='Profile_InsuranceApplicant.whatIsRelationship' />}
                saveEvent={props.doUpsertPolicy}
                cancelEvent={props.doGetPolicy}
                allowEdit={props.allowEdit}
            >
                <FormRadio
                    fieldCode='isSeller'
                    label={<FormattedMessage id='Profile_InsuranceApplicant.isSeller' />}
                    value={{ code: convertFromBoolean(props.policy?.secureMyOfferCustomInfo?.isSeller) }}
                    references={props.reference.references?.YesNo}
                    referencesEvent={() => props.reference.doGetReferences('YesNo')}
                    changeEvent={(value) =>
                        props.doReducePolicyElement({
                            code: 'secureMyOfferCustomInfo.isSeller',
                            value: convertToBoolean(value.code),
                        })
                    }
                    allowEdit
                />
                <FormDropDown
                    fieldCode='relationship'
                    label={<FormattedMessage id='Profile_InsuranceApplicant.relationship' />}
                    value={{ code: props.policy?.secureMyOfferCustomInfo?.applicantRelationshipToSellerCode }}
                    references={props.reference?.references?.SecureMyOffer?.ApplicantRelationshipsToSeller}
                    referencesEvent={() =>
                        props.reference.doGetReferences('SecureMyOffer.ApplicantRelationshipsToSeller')
                    }
                    changeEvent={(value) => {
                        props.doReducePolicyElement({
                            code: 'secureMyOfferCustomInfo.applicantRelationshipToSellerCode',
                            value: value.code,
                        });
                        props.doReducePolicyElement({
                            code: 'secureMyOfferCustomInfo.applicantRelationshipToSellerName',
                            value: value.value,
                        });
                    }}
                    allowEdit
                />
                {showOtherRelationship && (
                    <FormInput
                        fieldCode='otherRelationshipDescription'
                        label={<FormattedMessage id='Profile_InsuranceApplicant.other' />}
                        value={props.policy?.secureMyOfferCustomInfo?.otherRelationshipDescription}
                        changeEvent={(value) =>
                            props.doReducePolicyElement({
                                code: 'secureMyOfferCustomInfo.otherRelationshipDescription',
                                value,
                            })
                        }
                        allowEdit
                    />
                )}
            </Form>
        </React.Fragment>
    );
};

export default ProfileInsuranceApplicant;
